import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Maximize2, X, ChevronLeft, Boxes } from "lucide-react";
import { api, fmt } from "../lib/api";
import { SmartImage } from "../components/SmartImage";

/* ---------- Public flows index ---------- */
export function FlowsIndex() {
  const [flows, setFlows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/public/flows").then((r) => { setFlows(r.data.flows || []); setLoaded(true); }).catch(() => setLoaded(true)); }, []);
  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="overline mb-4">// FLOWS &amp; TOOLS</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-cream">Interactive flows</h1>
        <p className="text-textMuted mt-3 max-w-2xl">Hands-on tools and guided experiences — open them straight in your browser. Some are free to all; the deeper ones live inside the membership.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {flows.map((f) => (
            <Link key={f.id} to={`/flows/${f.id}`} className="block panel overflow-hidden hover:border-borderGoldHi group" data-testid={`flow-card-${f.id}`}>
              {(f.thumbnail_url || f.media_url) && (
                <div className="aspect-video bg-surface overflow-hidden border-b border-borderGold">
                  <SmartImage src={f.thumbnail_url || f.media_url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
                </div>
              )}
              <div className="p-5">
                <div className="overline mb-1 flex items-center gap-1"><Boxes className="w-3 h-3"/>{f.flow_embed === "iframe" ? "INTERACTIVE" : "TOOL"}{f.expires_at && ` · UNTIL ${fmt.date(f.expires_at).toUpperCase()}`}</div>
                <h3 className="font-display text-2xl text-cream">{f.title}</h3>
                {f.description && <p className="text-textMuted text-sm mt-1 line-clamp-3">{f.description}</p>}
              </div>
            </Link>
          ))}
          {loaded && flows.length === 0 && <p className="text-textMuted">No public flows are live right now. Check back soon.</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Public flow viewer ---------- */
export function FlowView() {
  const { id } = useParams();
  const [flow, setFlow] = useState(null);
  const [err, setErr] = useState(false);
  const [full, setFull] = useState(false);
  useEffect(() => { api.get(`/public/flows/${id}`).then((r) => setFlow(r.data.flow)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="min-h-screen flex items-center justify-center text-textMuted">This flow isn't available.</div>;
  if (!flow) return <div className="min-h-screen flex items-center justify-center text-textMuted">Loading…</div>;
  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-10 py-12">
      <div className="max-w-4xl mx-auto">
        <Link to="/flows" className="text-xs font-mono text-textMuted hover:text-gold flex items-center gap-1 mb-6"><ChevronLeft className="w-4 h-4"/>All flows</Link>
        <div className="overline mb-2 flex items-center gap-1"><Boxes className="w-3 h-3"/>FLOW{flow.expires_at && ` · AVAILABLE UNTIL ${fmt.date(flow.expires_at).toUpperCase()}`}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-cream">{flow.title}</h1>
        {flow.description && <p className="text-textMuted mt-3 max-w-2xl whitespace-pre-wrap">{flow.description}</p>}

        {flow.flow_embed === "link" && flow.flow_url && (
          <a href={flow.flow_url} target="_blank" rel="noreferrer" className="btn-gold inline-flex mt-8" data-testid="flow-open"><ExternalLink className="w-4 h-4"/>Open {flow.title}</a>
        )}
        {flow.flow_embed === "iframe" && flow.flow_url && (
          <div className="mt-8" data-testid="flow-iframe">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">// INTERACTIVE FLOW</div>
              <button onClick={() => setFull(true)} className="btn-ghost text-xs" data-testid="flow-expand"><Maximize2 className="w-3 h-3"/>Expand</button>
            </div>
            <div className="aspect-video w-full border border-borderGold bg-surface">
              <iframe src={flow.flow_url} title={flow.title} className="w-full h-full" allow="fullscreen; clipboard-write; encrypted-media"/>
            </div>
          </div>
        )}
      </div>
      {full && (
        <div className="fixed inset-0 z-[100] bg-void/95 flex flex-col" data-testid="flow-fullscreen">
          <div className="flex items-center justify-between px-4 py-3 border-b border-borderGold">
            <div className="overline truncate">// {flow.title}</div>
            <button onClick={() => setFull(false)} className="btn-ghost text-xs" data-testid="flow-close"><X className="w-4 h-4"/>Close</button>
          </div>
          <iframe src={flow.flow_url} title={flow.title} className="flex-1 w-full" allow="fullscreen; clipboard-write; encrypted-media"/>
        </div>
      )}
    </div>
  );
}
